import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "../../api/axios";
import { CardGridSkeleton } from "../Common/Loading/PageSkeletons";
import { useFeedbackMessage } from "../Common/Feedback/FeedbackHub";

const cardDescriptor = card => [card.set_name, card.collector_number, card.rarity].filter(Boolean).join(" · ");

function OpportunityCard({ opportunity, onPropose }) {
  const profilePath = `/u/${opportunity.handle || opportunity.user_id}`;
  const cardsIWant = opportunity.cards_i_want || [];
  const cardsTheyWant = opportunity.cards_they_want || [];
  const suggestedCards = opportunity.suggested_cards_for_them || [];
  const sharedCommunities = opportunity.shared_communities || [];
  const sharedEvents = opportunity.upcoming_shared_events || [];
  const mainReason = cardsIWant.length > 0
    ? `${opportunity.display_name} tiene ${cardsIWant.length} carta${cardsIWant.length === 1 ? "" : "s"} que buscas`
    : cardsTheyWant.length > 0
      ? `${opportunity.display_name} busca cartas que puedes tener`
      : `Hay señales de afinidad con ${opportunity.display_name}`;

  return (
    <Card className="cardora-card opportunity-card h-100">
      <Card.Body>
        <div className="opportunity-head">
          <Link to={profilePath} className="opportunity-person">
            {opportunity.avatar_url ? (
              <img src={mediaUrl(opportunity.avatar_url)} alt="" />
            ) : (
              <span>{opportunity.display_name?.slice(0, 1) || "C"}</span>
            )}
            <div>
              <strong>{opportunity.display_name}</strong>
              <small>{opportunity.handle ? `@${opportunity.handle}` : "Coleccionista"}</small>
            </div>
          </Link>
          <div className="opportunity-score">
            <b>{opportunity.compatibility_score || 0}%</b>
            <span>afinidad</span>
          </div>
        </div>

        <div className="opportunity-reason">
          <strong>{mainReason}</strong>
          <span>Cardora cruzo tus buscadas, tus cartas, fotos reales, comunidades y eventos cercanos.</span>
        </div>

        <div className="opportunity-pills">
          {cardsIWant.length > 0 && <Badge bg="success">{cardsIWant.length} carta{cardsIWant.length === 1 ? "" : "s"} que buscas</Badge>}
          {cardsTheyWant.length > 0 && <Badge bg="light" text="dark">{cardsTheyWant.length} busca de ti</Badge>}
          {suggestedCards.length > 0 && <Badge bg="light" text="dark">{suggestedCards.length} tuyas pueden interesarle</Badge>}
          {opportunity.real_photo_matches > 0 && <Badge bg="dark">{opportunity.real_photo_matches} con foto real</Badge>}
          {opportunity.reviews_count > 0 && <Badge bg="warning" text="dark">{opportunity.average_rating}/5 reputacion</Badge>}
        </div>

        {cardsIWant.length > 0 && (
          <div className="opportunity-section">
            <span>Cartas que buscas y esta persona tiene</span>
            <div className="opportunity-card-strip">
              {cardsIWant.slice(0, 4).map(card => (
                <Link to={`${profilePath}?tab=binder&card=${card.user_card_id}`} className="opportunity-mini-card" key={card.user_card_id}>
                  {card.image_url && <img src={mediaUrl(card.image_url)} alt="" />}
                  <small>
                    <strong>{card.name}</strong>
                    <em>{cardDescriptor(card) || card.status || "En binder"}</em>
                  </small>
                </Link>
              ))}
            </div>
          </div>
        )}

        {cardsTheyWant.length > 0 && (
          <div className="opportunity-section">
            <span>Lo que esa persona busca</span>
            <div className="opportunity-wanted-list">
              {cardsTheyWant.slice(0, 3).map(card => (
                <small key={card.user_card_id}>{card.name}{card.collector_number ? ` · ${card.collector_number}` : ""}</small>
              ))}
            </div>
          </div>
        )}

        {suggestedCards.length > 0 && (
          <div className="opportunity-section">
            <span>Cartas tuyas que podrian interesarle</span>
            <div className="opportunity-card-strip">
              {suggestedCards.slice(0, 4).map(card => (
                <div className="opportunity-mini-card" key={card.user_card_id}>
                  {card.image_url && <img src={mediaUrl(card.image_url)} alt="" />}
                  <small>
                    <strong>{card.name}</strong>
                    <em>{card.reason || cardDescriptor(card) || "posible interes"}</em>
                  </small>
                </div>
              ))}
            </div>
          </div>
        )}

        {sharedEvents.length > 0 && (
          <div className="opportunity-section">
            <span>Eventos donde podrian cruzarse</span>
            <div className="opportunity-wanted-list">
              {sharedEvents.slice(0, 3).map(event => (
                <small key={event.id}>{event.name} · {event.business_name}{event.starts_at ? ` · ${new Date(event.starts_at).toLocaleDateString("es-ES")}` : ""}</small>
              ))}
            </div>
          </div>
        )}

        {sharedCommunities.length > 0 && (
          <div className="opportunity-section">
            <span>Comunidades en comun</span>
            <div className="opportunity-communities">
              {sharedCommunities.slice(0, 3).map(community => (
                <Link to={`/store/${community.slug}`} key={community.id}>{community.name}</Link>
              ))}
            </div>
          </div>
        )}

        <div className="opportunity-actions">
          <Button as={Link} to={`${profilePath}?tab=binder`} className="btn-cardora">Ver carta / binder</Button>
          <Button as={Link} to={profilePath} variant="outline-dark">Ver perfil</Button>
          {cardsIWant.length > 0 && (
            <Button type="button" variant="outline-dark" onClick={() => onPropose(opportunity)}>
              Proponer intercambio
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

function OpportunitiesHome() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [proposal, setProposal] = useState(null);
  const [proposalForm, setProposalForm] = useState({
    offeredUserCardIds: [],
    requestedUserCardIds: [],
    tradeDayId: "",
    message: ""
  });
  const [proposalError, setProposalError] = useState("");

  const loadOpportunities = () => {
    setLoading(true);
    api
      .get("/social/opportunities")
      .then(({ data }) => setOpportunities(data.opportunities || []))
      .catch(() => {
        setNotice("No se pudieron cargar oportunidades.");
        setOpportunities([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadOpportunities();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), 4200);
    return () => clearTimeout(timer);
  }, [notice]);

  useFeedbackMessage(notice, notice.startsWith("No") ? "danger" : "success");

  const summary = useMemo(() => ({
    people: opportunities.length,
    cards: opportunities.reduce((sum, item) => sum + Number(item.has_cards_i_want || 0), 0),
    events: opportunities.reduce((sum, item) => sum + Number(item.upcoming_shared_events?.length || 0), 0)
  }), [opportunities]);

  const refreshNotifications = async () => {
    setNotice("");
    try {
      const { data } = await api.post("/social/opportunities/refresh", { limit: 20 });
      setOpportunities(data.opportunities || []);
      setNotice(data.notificationsCreated > 0
        ? `${data.notificationsCreated} oportunidad${data.notificationsCreated === 1 ? "" : "es"} enviada${data.notificationsCreated === 1 ? "" : "s"} a notificaciones.`
        : "Oportunidades actualizadas. No habia avisos nuevos.");
    } catch {
      setNotice("No se pudieron actualizar las notificaciones.");
    }
  };

  const openProposal = opportunity => {
    const validEvents = (opportunity.upcoming_shared_events || []).filter(event =>
      event.event_type === "trade_day"
      && ["attending", "checked_in"].includes(event.viewer_status)
      && ["attending", "checked_in"].includes(event.target_status)
    );
    setProposal(opportunity);
    setProposalError("");
    setProposalForm({
      offeredUserCardIds: (opportunity.suggested_cards_for_them || []).slice(0, 4).map(card => card.user_card_id),
      requestedUserCardIds: (opportunity.cards_i_want || []).slice(0, 4).map(card => card.user_card_id),
      tradeDayId: validEvents[0]?.id || "",
      message: ""
    });
  };

  const toggleProposalCard = (field, cardId) => {
    setProposalForm(current => {
      const values = new Set(current[field] || []);
      if (values.has(cardId)) values.delete(cardId);
      else values.add(cardId);
      return { ...current, [field]: Array.from(values) };
    });
  };

  const submitProposal = async event => {
    event.preventDefault();
    if (!proposalForm.requestedUserCardIds.length) {
      setProposalError("Selecciona al menos una carta que te interesa.");
      return;
    }
    try {
      await api.post("/trades", {
        receiverId: proposal.user_id,
        operationType: "trade",
        offeredUserCardIds: proposalForm.offeredUserCardIds,
        requestedUserCardIds: proposalForm.requestedUserCardIds,
        tradeDayId: proposalForm.tradeDayId || null,
        message: proposalForm.message
      });
      setProposal(null);
      setNotice("Propuesta enviada. La otra persona recibira una notificacion y un correo si lo tiene activo.");
    } catch (error) {
      setProposalError(error.response?.data?.error || "No se pudo enviar la propuesta.");
    }
  };

  return (
    <Container className="page-band opportunities-page">
      <div className="community-directory-header mb-4">
        <div>
          <h1 className="section-title mb-1">Oportunidades</h1>
          <p className="muted-copy mb-0">Personas con cartas que buscas, cartas tuyas que pueden interesarles y eventos donde podrian cruzarse.</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-dark" onClick={loadOpportunities}>Actualizar</Button>
          <Button className="btn-cardora" onClick={refreshNotifications}>Crear avisos</Button>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col sm={4}><div className="store-metric"><span>Personas</span><strong>{summary.people}</strong></div></Col>
        <Col sm={4}><div className="store-metric"><span>Cartas que buscas</span><strong>{summary.cards}</strong></div></Col>
        <Col sm={4}><div className="store-metric"><span>Eventos en comun</span><strong>{summary.events}</strong></div></Col>
      </Row>


      {loading ? (
        <CardGridSkeleton count={6} />
      ) : opportunities.length === 0 ? (
        <Card className="cardora-card">
          <Card.Body>
            <Card.Title>Todavia no hay oportunidades claras</Card.Title>
            <p className="muted-copy">Carga tus cartas buscadas y al menos algunas cartas reales en tu binder. Cuando asistas a eventos o sigas comunidades, Cardora tendra mas senales para cruzarte con otros coleccionistas.</p>
            <div className="d-flex flex-wrap gap-2">
              <Button as={Link} to="/binder" className="btn-cardora">Agregar buscada</Button>
              <Button as={Link} to="/communities" variant="outline-dark">Ver comunidades</Button>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Row className="g-3">
          {opportunities.map(opportunity => (
            <Col lg={6} xl={4} key={opportunity.user_id}>
              <OpportunityCard opportunity={opportunity} onPropose={openProposal} />
            </Col>
          ))}
        </Row>
      )}

      <Modal show={Boolean(proposal)} onHide={() => setProposal(null)} centered size="lg">
        <Form onSubmit={submitProposal}>
          <Modal.Header closeButton>
            <Modal.Title>Proponer intercambio</Modal.Title>
          </Modal.Header>
          {proposal && (
            <Modal.Body>
              <p className="muted-copy">
                Revisa las cartas y propone un punto de encuentro. No se gestiona dinero dentro de Cardora.
              </p>
              {proposalError && <Alert variant="danger">{proposalError}</Alert>}
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Te interesan</Form.Label>
                  <div className="d-grid gap-2">
                    {(proposal.cards_i_want || []).map(card => (
                      <Form.Check
                        type="checkbox"
                        id={`requested-${card.user_card_id}`}
                        key={card.user_card_id}
                        checked={proposalForm.requestedUserCardIds.includes(card.user_card_id)}
                        onChange={() => toggleProposalCard("requestedUserCardIds", card.user_card_id)}
                        label={`${card.name}${card.collector_number ? ` � ${card.collector_number}` : ""}`}
                      />
                    ))}
                  </div>
                </Col>
                <Col md={6}>
                  <Form.Label>Puedes llevar</Form.Label>
                  <div className="d-grid gap-2">
                    <div className="trade-card-picker-grid opportunity-trade-picker">
                      {(proposal.suggested_cards_for_them || []).map(card => {
                        const selected = proposalForm.offeredUserCardIds.includes(card.user_card_id);
                        return (
                          <button type="button" className={selected ? "is-selected" : ""} onClick={() => toggleProposalCard("offeredUserCardIds", card.user_card_id)} key={card.user_card_id}>
                            {card.image_url
                              ? <img src={mediaUrl(card.image_url)} alt={card.name} />
                              : <span className="trade-picker-placeholder"><i className="bi bi-card-image" /></span>}
                            <span><strong>{card.name}</strong><small>{[card.set_name, card.collector_number].filter(Boolean).join(" - ") || "Sin detalles"}</small></span>
                            <i className={selected ? "bi bi-check-circle-fill" : "bi bi-plus-circle"} />
                          </button>
                        );
                      })}
                    </div>
                    {(proposal.suggested_cards_for_them || []).length === 0 && (
                      <small className="muted-copy">Puedes enviar la propuesta y decidir que llevar mas adelante.</small>
                    )}
                  </div>
                </Col>
                <Col xs={12}>
                  <Form.Label>Trade Day compartido, opcional</Form.Label>
                  <Form.Select
                    value={proposalForm.tradeDayId}
                    onChange={event => setProposalForm(current => ({ ...current, tradeDayId: event.target.value }))}
                  >
                    <option value="">Acordar lugar despues</option>
                    {(proposal.upcoming_shared_events || [])
                      .filter(event => event.event_type === "trade_day"
                        && ["attending", "checked_in"].includes(event.viewer_status)
                        && ["attending", "checked_in"].includes(event.target_status))
                      .map(event => (
                        <option value={event.id} key={event.id}>
                          {event.name} � {event.business_name}
                        </option>
                      ))}
                  </Form.Select>
                </Col>
                <Col xs={12}>
                  <Form.Label>Mensaje</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    maxLength={1000}
                    value={proposalForm.message}
                    onChange={event => setProposalForm(current => ({ ...current, message: event.target.value }))}
                    placeholder="Ej. Me interesa tu Pikachu. Puedo llevar estas cartas al proximo encuentro."
                  />
                </Col>
              </Row>
            </Modal.Body>
          )}
          <Modal.Footer>
            <Button variant="outline-dark" onClick={() => setProposal(null)}>Cerrar</Button>
            <Button type="submit" className="btn-cardora">Enviar propuesta</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default OpportunitiesHome;
